<template>
  <ul class="nav nav-pills flex-column list-unstyled mb-0 flex-shrink-0">
    <SidenavElement
      itemClass="nav-item mb-2"
      :to="{ name: 'MarketInsights' }"
      :isActive="isMarketInsightsActive"
      icon="fa-solid fa-magnifying-glass-chart"
      :text="$t('market_insights.title')"
    />

    <SidenavElement
      v-if="showSolderaInventory"
      itemClass="nav-item mb-2"
      :to="{ name: 'SolderaInventory' }"
      :isActive="isSolderaInventoryActive"
      icon="fa-solid fa-warehouse"
      :text="$t('soldera_inventory.title')"
    />

    <SidenavElement
      v-if="showEcolabels"
      itemClass="nav-item mb-2"
      :to="{ name: 'Ecolabels' }"
      :isActive="isEcolabelsActive"
      icon="fa-solid fa-leaf"
      :text="$t('ecolabels.title')"
    />
  </ul>

  <CompanySidenavList :accountData>
    <template v-if="showComplianceGroups" #sections>
      <SidenavElement
        itemClass="nav-item mb-1"
        :isActive="false"
        :disableHover="true"
      >
        <div class="compliance-groups-section text-uppercase">
          Compliance Groups
        </div>
      </SidenavElement>
      <SidenavElement
        v-for="entry in complianceGroupEntries"
        :key="entry.key"
        itemClass="nav-item mb-2"
        :to="{ name: 'ComplianceGroup', params: { group: entry.key } }"
        :isActive="isComplianceRoute && route.params.group === entry.key"
        icon="fa-solid fa-layer-group"
        :text="entry.title"
        :hasAction="entry.openActions > 0"
      />
    </template>
  </CompanySidenavList>

  <div
    class="d-flex align-items-center flex-shrink-0"
    :class="{
      'justify-content-between': accountData.admin_account_id,
      'justify-content-start': !accountData.admin_account_id,
    }"
  >
    <RouterLink
      class="btn btn-lg btn-link ps-3 text-gray-400"
      :to="{ name: 'RoleSelection' }"
    >
      <font-awesome-icon icon="plus" class="me-2" />
      {{ $t("dashboard.add_company_or_device") }}
    </RouterLink>
    <AdminCompanyAccessButton
      v-if="accountData.admin_account_id"
      :accountData
    />
  </div>
</template>

<script setup lang="ts">
import { useComplianceDemoStore } from "@/compliance_groups/useComplianceDemoStore";
import AdminCompanyAccessButton from "@/components/layout/AdminCompanyAccessButton.vue";
import CompanySidenavList from "@/components/layout/CompanySidenavList.vue";
import SidenavElement from "@/components/layout/SidenavElement.vue";
import { useAccountFeatureFlags } from "@/composables/useAccountFeatureFlags";
import type { Account } from "@/types";
import { computed } from "vue";
import { RouterLink, useRoute } from "vue-router";

// The full Soldera producer/corporate sidenav: market insights, Soldera inventory (flag-gated), ecolabels
// (flag-gated), the company list with consolidated view and follow-ups, and
// the add-company footer.
const props = defineProps<{
  accountData: Account;
}>();

const route = useRoute();

const isMarketInsightsActive = computed(() => {
  return route.name === "MarketInsights";
});

const isSolderaInventoryActive = computed(() => {
  return route.path.startsWith("/soldera-inventory");
});

const isEcolabelsActive = computed(() => {
  return route.name === "Ecolabels";
});

const { hasFlag: hasAccountFlag } = useAccountFeatureFlags(props.accountData);

// Check if Ecolabels should be shown:
// - ecolabels_enabled company flag OR ekoenergy_admin_enabled account flag
const showEcolabels = computed(() => {
  const hasAccountEkoenergyAdmin = hasAccountFlag(
    "ekoenergy_admin_enabled"
  ).value;
  const hasCompanyEcolabels = props.accountData.companies.some(
    (c) => c.feature_flags?.ecolabels_enabled
  );
  return hasAccountEkoenergyAdmin || hasCompanyEcolabels;
});

// Soldera Inventory: opened per account by an admin via the realtime_soldera_inventory flag; the
// backend refuses the page's endpoints for any other account.
const showSolderaInventory = hasAccountFlag("realtime_soldera_inventory");

// Compliance Groups demo (account flag): rendered as its own section of the company list,
// between the consolidated view and the company search. The entries read the open-item count
// from the demo store so the action dot mirrors what the group page shows; no count badges,
// since a domain count says nothing actionable about a group.
const showComplianceGroups = hasAccountFlag("compliance_groups_demo_enabled");
const complianceStore = useComplianceDemoStore();
const complianceGroupEntries = computed(() =>
  (["eecs", "rego"] as const).map((key) => ({
    key,
    title: complianceStore.rollups[key].meta.shortTitle,
    openActions: complianceStore.rollups[key].openActions,
  }))
);
const isComplianceRoute = computed(() =>
  String(route.name ?? "").startsWith("ComplianceGroup")
);
</script>

<style scoped lang="scss">
// Same chrome as the companies search header in CompanySidenavList: a hairline rule above,
// then an uppercase section label.
.compliance-groups-section {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.72);
}

.btn-link {
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.text-gray-400 {
  transition: color 0.15s ease-in-out;

  &:hover {
    color: var(--phoenix-gray-200) !important;
  }
}
</style>
